import { useMemo, useState } from "react";
import { useAdminClients, useAdminOrders } from "@/api/useAdmin";
import { useReleaseOrder } from "@/api/adminMutations";
import {
  useTransitionOrder,
  type OrderStatusLower,
  type TransitionOrderArgs,
} from "@/api/adminOrdersMutations";
import { AdminHeader } from "./AdminHeader";
import { AdminKpis } from "./AdminKpis";
import { StatusPill } from "@/components/StatusPill";
import { Dialog } from "@/components/Dialog";
import { Button } from "@/components/Button";
import { AlertIcon, CheckIcon } from "@/components/icons";

// Per-status action affordances. Mirrors the TRANSITIONS table on the
// server (backend/src/routes/orders.ts); a button is only shown if the
// transition exists. Cancel-from-production is destructive — gated
// behind a confirm dialog.
type Action = {
  label: string;
  to: OrderStatusLower;
  variant: "primary" | "danger";
  confirm?: boolean;
};
const ACTIONS_BY_STATUS: Record<string, Action[]> = {
  draft:      [{ label: "Cancel",          to: "cancelled", variant: "danger"  }],
  proof:      [{ label: "Cancel",          to: "cancelled", variant: "danger"  }],
  // 'hold' release lives on the existing /release endpoint (rendered by
  // the approval-queue callout above) — the Cancel here uses the new
  // state-machine endpoint.
  hold:       [{ label: "Cancel",          to: "cancelled", variant: "danger"  }],
  production: [
    { label: "Received by factory", to: "factory_received", variant: "primary" },
    { label: "Cancel",              to: "cancelled",        variant: "danger", confirm: true },
  ],
  factory_received: [
    { label: "Packed", to: "packed",    variant: "primary" },
    { label: "Cancel", to: "cancelled", variant: "danger", confirm: true },
  ],
  packed: [
    { label: "Mark dispatched", to: "shipped",   variant: "primary" },
    { label: "Cancel",          to: "cancelled", variant: "danger", confirm: true },
  ],
  shipped:    [{ label: "Mark delivered",  to: "delivered", variant: "primary" }],
  delivered:  [],
  cancelled:  [],
};

/**
 * Cross-tenant orders & designs view. Hold-queue callout at the top
 * lists orders parked above the volume gate (£4k) awaiting admin
 * sign-off before they hit the press. Filterable by client + status.
 */
export function OrdersAdminPage() {
  const { data: orders = [], isLoading } = useAdminOrders();
  const { data: clients = [] } = useAdminClients();
  const release = useReleaseOrder();
  const transition = useTransitionOrder();
  const [q, setQ] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  // Confirm dialog for destructive transitions (cancel-from-production).
  const [confirmArgs, setConfirmArgs] = useState<TransitionOrderArgs | null>(null);

  async function runTransition(args: TransitionOrderArgs) {
    setTransitionError(null);
    try {
      await transition.mutateAsync(args);
    } catch (e) {
      setTransitionError(`${args.orderId}: ${(e as Error).message}`);
    }
  }

  const holds = orders.filter((o) => o.status === "hold");

  const filtered = useMemo(
    () =>
      orders.filter(
        (o) =>
          (clientFilter === "all" || o.clientId === clientFilter) &&
          (statusFilter === "all" || o.status === statusFilter) &&
          (!q ||
            o.id.toLowerCase().includes(q.toLowerCase()) ||
            o.kitTitle.toLowerCase().includes(q.toLowerCase())),
      ),
    [orders, q, clientFilter, statusFilter],
  );

  const clientName = (id: string) => clients.find((c) => c.id === id)?.name ?? id;

  return (
    <div className="mx-auto max-w-[1480px] px-8 py-8">
      <AdminHeader
        eyebrow="Studio admin · Orders"
        title="Orders & designs across clients"
        subtitle="Every order and design from every client. Filter by client, by status, or by ID. Hold queue at the top needs your sign-off before print."
      />

      <AdminKpis />

      {holds.length > 0 && (
        <div className="mb-5 overflow-hidden rounded-lg border border-[var(--admin-border,var(--admin-tint))] bg-white">
          <div className="flex items-center justify-between border-b border-[var(--admin-border,var(--admin-tint))] bg-[var(--admin-wash,#F9F7FD)] px-4 py-3">
            <h3 className="m-0 flex items-center gap-2 text-sm font-semibold text-fg-1">
              <AlertIcon className="text-[var(--admin)]" />
              Approval queue · {holds.length} order{holds.length === 1 ? "" : "s"} on hold
            </h3>
            <span className="text-xs text-fg-3">Volume gate &gt; £4,000</span>
          </div>
          <div className="bg-bg-1 p-3">
            {holds.map((h) => (
              <div
                key={h.id}
                className="mb-2 grid grid-cols-[1fr_auto] items-center gap-3 rounded-md border border-[var(--admin-border,var(--admin-tint))] border-l-[3px] border-l-[var(--admin)] bg-white p-3 last:mb-0"
              >
                <div className="text-xs text-fg-3">
                  <div>
                    <strong className="text-fg-1 text-sm">{h.id}</strong>{" "}
                    · {clientName(h.clientId)} · {h.kitTitle}
                  </div>
                  <div className="mt-1 tabular-nums">
                    Qty {h.qty} · £{h.total.toFixed(2)} · placed {h.placed} by {h.proofBy}
                  </div>
                  <div className="mt-1 font-semibold text-[var(--admin-dark)]">{h.holdReason}</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    title="Cancel-hold flow lands in Phase 4 alongside the print-ops cancel email"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    loading={release.isPending}
                    onClick={async () => {
                      setReleaseError(null);
                      try {
                        await release.mutateAsync(h.id);
                      } catch (e) {
                        setReleaseError(`${h.id}: ${(e as Error).message}`);
                      }
                    }}
                    leadingIcon={<CheckIcon width={12} height={12} strokeWidth={2.5} />}
                    className="bg-[var(--admin)] hover:bg-[var(--admin-dark)] active:bg-[var(--admin-dark)]"
                  >
                    {release.isPending ? "Releasing…" : "Release"}
                  </Button>
                </div>
              </div>
            ))}
            {releaseError && (
              <div className="mt-2 rounded border-l-[3px] border-[var(--danger)] bg-[#FFF8F7] p-2.5 text-xs text-[color:var(--danger-fg)]">
                Release failed: {releaseError}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            placeholder="Search by order ID or kit…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="min-w-[260px] rounded border border-[var(--border-2)] bg-white px-3 py-2 text-sm outline-none focus-visible:border-fg-1 focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          />
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="rounded border border-[var(--border-2)] bg-white px-3 py-2 text-sm outline-none focus-visible:border-fg-1 focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          >
            <option value="all">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-[var(--border-2)] bg-white px-3 py-2 text-sm outline-none focus-visible:border-fg-1 focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          >
            <option value="all">All statuses</option>
            {["draft", "proof", "hold", "production", "factory_received", "packed", "shipped", "delivered", "cancelled"].map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-fg-3">
          {filtered.length} of {orders.length} orders
        </div>
      </div>

      {transitionError && (
        <div className="mb-2 rounded border-l-[3px] border-[var(--danger)] bg-[#FFF8F7] p-2.5 text-xs text-[color:var(--danger-fg)]">
          Transition failed: {transitionError}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
        {isLoading ? (
          <div className="p-6 text-fg-3">Loading…</div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--border-1)] bg-bg-2 text-left">
                {["Order", "Client", "Kit", "Qty", "Total", "Status", "Placed", "Approved by", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2 text-xxs font-semibold uppercase tracking-wider text-fg-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const actions = ACTIONS_BY_STATUS[o.status] ?? [];
                // royalMailTracking is surfaced by the admin orders endpoint;
                // read defensively so the code lights up once the hook passes it through.
                const rmTracking = (o as { royalMailTracking?: string | null }).royalMailTracking;
                return (
                  <tr
                    key={o.id}
                    className="border-b border-[var(--border-1)] last:border-0 hover:bg-[var(--admin-wash,#F9F7FD)]"
                  >
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold tabular-nums">{o.id}</td>
                    <td className="px-3 py-2.5 text-sm">{clientName(o.clientId)}</td>
                    <td className="px-3 py-2.5 text-sm">
                      {o.kitTitle}
                      <div className="font-mono text-xxs tabular-nums text-fg-3">{o.kit}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm tabular-nums">{o.qty}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm tabular-nums">
                      £{o.total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusPill status={o.status} />
                      {rmTracking && (
                        <div className="mt-1 font-mono text-xxs tabular-nums text-fg-3" title="Royal Mail return tracking">
                          {rmTracking}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-sm tabular-nums text-fg-3">{o.placed}</td>
                    <td className="px-3 py-2.5 text-sm text-fg-3">{o.proofBy}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {actions.map((a) => {
                          const args: TransitionOrderArgs = { orderId: o.id, to: a.to };
                          const onClick = () => {
                            if (a.confirm) setConfirmArgs(args);
                            else void runTransition(args);
                          };
                          const busy = transition.isPending && transition.variables?.orderId === o.id && transition.variables?.to === a.to;
                          if (a.variant === "primary") {
                            return (
                              <Button
                                key={a.label}
                                size="sm"
                                disabled={transition.isPending}
                                onClick={onClick}
                                leadingIcon={<CheckIcon width={11} height={11} strokeWidth={2.5} />}
                                className="bg-[var(--admin)] hover:bg-[var(--admin-dark)] active:bg-[var(--admin-dark)]"
                              >
                                {busy ? "Saving…" : a.label}
                              </Button>
                            );
                          }
                          // Quiet destructive button for Cancel.
                          return (
                            <Button
                              key={a.label}
                              variant="danger"
                              size="sm"
                              disabled={transition.isPending}
                              onClick={onClick}
                            >
                              {busy ? "Saving…" : a.label}
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog
        open={confirmArgs !== null}
        onOpenChange={(open) => { if (!open) setConfirmArgs(null); }}
        title="Cancel order in production?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmArgs(null)}>
              Keep order
            </Button>
            <Button
              variant="danger"
              disabled={transition.isPending}
              onClick={async () => {
                if (!confirmArgs) return;
                const args = confirmArgs;
                setConfirmArgs(null);
                await runTransition(args);
              }}
            >
              Cancel order
            </Button>
          </>
        }
      >
        <p className="m-0">
          Order <strong className="font-mono">{confirmArgs?.orderId}</strong> is already in production. Cancelling now will
          flag a WARN in the audit log and the print-ops team will need to halt the run.
        </p>
        <p className="mt-3 text-fg-3">This action can't be undone.</p>
      </Dialog>
    </div>
  );
}
